export const TAE_FORMULARIO = 12.7;

const numberFromValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === '') return 0;

  let normalized = String(value).replace(/[€\s]/g, '').trim();
  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else if (/^-?[1-9]\d{0,2}(?:\.\d{3})+$/.test(normalized)) {
    // También llegan importes españoles sin decimales, por ejemplo 1.395.
    normalized = normalized.replace(/\./g, '');
  }
  normalized = normalized.replace(/[^0-9.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumCredits = (credits, key) =>
  (Array.isArray(credits) ? credits : []).reduce(
    (sum, credit) => sum + numberFromValue(credit?.[key]),
    0
  );

export const calculateEndeudamiento = ({
  creditos = [],
  primerTitular = {},
  segundoTitular = {},
  gastosVivienda = {},
  otrosDatos = {},
  honorarios,
  plazoMeses = 120,
  tasaAnual = TAE_FORMULARIO,
  incluirSeguro = false,
} = {}) => {
  const ingresos =
    numberFromValue(primerTitular.salarioNetoMensual) +
    numberFromValue(segundoTitular.salarioNetoMensual);
  const capitalPendiente = sumCredits(creditos, 'capitalPendiente');
  const cuotaCreditos = sumCredits(creditos, 'cuotaMensual');
  const otrasCuotas = numberFromValue(otrosDatos.importeMensual);
  const vivienda = gastosVivienda.tipoVivienda === 'alquiler'
    ? numberFromValue(gastosVivienda.importeAlquiler)
    : gastosVivienda.tipoVivienda === 'propiedadConHipoteca'
      ? numberFromValue(gastosVivienda.importeHipoteca)
      : 0;
  const cuotaActual = cuotaCreditos + otrasCuotas + vivienda;
  const meses = Math.max(1, Math.round(numberFromValue(plazoMeses) || 120));
  // La tabla de amortización usa la tasa anual indicada repartida entre sus
  // doce cuotas mensuales. Así coincide con la cuota de referencia que usa
  // operaciones al comparar ofertas.
  const tasaMensual = Math.max(0, numberFromValue(tasaAnual)) / 100 / 12;
  // The offer includes a default 1% adjustment on the debt being reunited.
  const capitalAReunificar = capitalPendiente * 1.01;
  // Operations applies a 5% fee by default, but an explicitly entered value
  // (including zero) must always win.
  const honorariosCalculados = honorarios === undefined || honorarios === '' || honorarios === null
    ? Math.floor((capitalAReunificar * 0.05 + Number.EPSILON) * 100) / 100
    : Math.max(0, numberFromValue(honorarios));
  const capitalSinSeguro = capitalAReunificar + honorariosCalculados;
  // Seguro mensual sobre capital, ajuste del 1 % y honorarios (Diego, 23/10).
  const importeSeguro = Math.round((capitalSinSeguro * 0.0027 + Number.EPSILON) * 100) / 100;
  const factorCuota = tasaMensual === 0
    ? 1 / meses
    : tasaMensual / (1 - ((1 + tasaMensual) ** -meses));
  const cuotaSinSeguro = capitalSinSeguro * factorCuota;
  const cuotaConSeguro = cuotaSinSeguro + importeSeguro;
  const capitalFinanciado = capitalSinSeguro;
  const cuotaNueva = incluirSeguro ? cuotaConSeguro : cuotaSinSeguro;
  const cuotaNuevaTotal = cuotaNueva + otrasCuotas + vivienda;

  return {
    ingresos,
    vivienda,
    otrasCuotas,
    cuotaCreditos,
    cuotaActual,
    ratioActual: ingresos > 0 ? (cuotaActual / ingresos) * 100 : null,
    capitalPendiente,
    capitalAReunificar,
    honorarios: honorariosCalculados,
    incluirSeguro: !!incluirSeguro,
    importeSeguro,
    capitalSinSeguro,
    cuotaSinSeguro,
    cuotaConSeguro,
    capitalFinanciado,
    plazoMeses: meses,
    tasaAnual: Math.max(0, numberFromValue(tasaAnual)),
    cuotaNueva,
    diferenciaCuotas: cuotaCreditos - cuotaNueva,
    cuotaNuevaTotal,
    ratioNuevo: ingresos > 0 ? (cuotaNuevaTotal / ingresos) * 100 : null,
  };
};
